import { getLocaleConstants } from "@tosize/ui";
import { match } from "ts-pattern";
import { z } from "zod";
import { sendThisToHemlockSchema, subjects } from "./output";

const innerObject = z.object({
	category: z
		.literal("general")
		.or(z.literal("order"))
		.or(z.literal("technical_issue"))
		.or(z.literal("business"))
		.or(z.literal("callback")),
});

const sharedObject = z.object({
	name: z.string().min(1),
	email: z.string().email().min(1),
	description: z.string().min(1),
	files: z.array(z.custom<File>((v) => v instanceof File)).min(1),
});

export type ContactCategory = z.infer<typeof innerObject>["category"];

export type ContactSubject =
	| z.infer<typeof product_specifications_general>["subject"]
	| z.infer<typeof quote_request>["subject"]
	| z.infer<typeof product_specifications_furniture>["subject"]
	| z.infer<typeof not_available>["subject"]
	| z.infer<typeof order_change>["subject"]
	| z.infer<typeof payment_failed>["subject"]
	| z.infer<typeof website_issue>["subject"]
	| z.infer<typeof general_question>["subject"]
	| z.infer<typeof business>["subject"]
	| z.infer<typeof callback>["subject"]
	| (typeof subjects)[number];

export type ContactProductTypes = z.infer<typeof productTypeSchema>["product_type"];

const urlArray = z.preprocess((val) => {
	if (Array.isArray(val)) {
		const lastItemIndex = val.reduce((acc, item, index) => (item ? index : acc), 0);

		return val.slice(0, Number(lastItemIndex) + 1);
	}

	return val;
}, z.array(z.string().url()).min(1));

const product_specifications_general = z.object({
	subject: z.literal("product_specifications_general"),
	product_urls: urlArray,
});

const quote_request = z.object({
	subject: z.literal("quote_request"),
	delivery_date: z.string().min(1),
});

const product_specifications_furniture = z.object({
	subject: z.literal("product_specifications_furniture"),
	product_materials: z.string().min(1),
	product_width: z.number().int().positive().min(1),
	product_height: z.number().int().positive().min(1),
	product_depth: z.number().int().positive().min(1),
	product_thickness: z.number().int().positive().min(1),
	url: z.string().url().min(1),
});

const not_available = z.object({
	subject: z.literal("not_available"),
});

const order_change = z.object({
	subject: z.literal("order_change"),
});

const payment_failed = z.object({
	subject: z.literal("payment_failed"),
});

const website_issue = z.object({
	subject: z.literal("website_issue"),
	url: z.string().url().min(1),
});

const general_question = z.object({
	subject: z.literal("general_question"),
});

const business = z.object({
	subject: z.literal("business"),
	company: z.string().min(1),
});

const callback = z.object({
	subject: z.literal("callback"),
	call_time: z.string().min(1),
});

const productTypeSchema = z.discriminatedUnion("product_type", [
	z.object({
		product_type: z.literal("sheet"),
		product_urls: urlArray,
	}),
	z.object({
		product_type: z.literal("cabinet"),
		product_materials: z.string().min(1),
		product_width: z.number().int().positive().min(1),
		product_height: z.number().int().positive().min(1),
		product_depth: z.number().int().positive().min(1),
		product_thickness: z.number().int().positive().min(1),
		url: z.string().url().min(1),
	}),
	z.object({
		product_type: z.literal("sample"),
	}),
	z.object({
		product_type: z.literal("accessory"),
		product_urls: urlArray,
	}),
]);

const phoneTransform = (data?: string, ctx?: z.RefinementCtx) => {
	const pattern = /^\+?(\d[\d\s().-]{7,})$/;

	const isInvalid =
		data !== undefined && data !== ""
			? data.includes("12345678") ||
				data.includes("87654321") ||
				data.includes("00000000") ||
				!pattern.test(data)
			: false;

	if (isInvalid) {
		ctx?.addIssue({ code: z.ZodIssueCode.custom, params: { type: "phone" } });
		return z.NEVER;
	}

	return data;
};

export function getSchema(subject?: ContactSubject) {
	if (subject && subjects.includes(subject)) {
		return innerObject.and(sendThisToHemlockSchema);
	}

	const schema = match(subject)
		.with(
			"product_specifications_general",
			"quote_request",
			"product_specifications_furniture",
			"not_available",
			"payment_failed",
			"website_issue",
			"general_question",
			() => sharedObject.extend({ phone: z.string().trim().optional().transform(phoneTransform) }),
		)
		.with("business", "callback", () =>
			sharedObject.extend({ phone: z.string().trim().min(1).transform(phoneTransform) }),
		)
		.otherwise(() => undefined);

	const subjectSchema = z.discriminatedUnion("subject", [
		product_specifications_general,
		quote_request,
		product_specifications_furniture,
		not_available,
		order_change,
		payment_failed,
		website_issue,
		general_question,
		business,
		callback,
	]);

	if (subject === "quote_request" || subject === "callback") {
		return schema
			? schema.and(innerObject).and(subjectSchema).and(productTypeSchema)
			: innerObject.and(subjectSchema).and(productTypeSchema);
	}

	if (schema) {
		return schema.and(innerObject).and(subjectSchema);
	}

	return innerObject.and(subjectSchema);
}

export function getCategorySubjects(lang: string) {
	const localeConstants = getLocaleConstants(lang);

	return {
		general: [
			"product_specifications_general",
			"product_specifications_furniture",
			"quote_request",
			"not_available",
			"general_question",
		],
		order: [
			"order_change",
			"order_product_complaint",
			"order_product_not_delivered",
			// 'order_product_return',
			// 'order_delivery',
			"general_question",
		],
		technical_issue: ["payment_failed", "website_issue", "general_question"],
		business: "business",
		...(localeConstants.customerServicePhone ? { callback: "callback" } : {}),
	} satisfies Partial<Record<ContactCategory, ContactSubject | ContactSubject[]>>;
}
